import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, Alert, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Grid, useTheme, IconButton } from "@mui/material";
import { tokens } from "../theme";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from "react";

const UploadDatasetModal = ({ open, onClose }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        category: "Oncology",
        privacy: "De-identified",
        institution: "Hospital",
        reb: false
    });

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'reb' ? checked : value
        }));
    };

    const handleUpload = () => {
        setLoading(true);
        // Mock upload delay
        setTimeout(() => {
            setLoading(false);
            onClose();
            // In a real app, this would trigger a refresh or add to the list
            console.log("Uploaded Dataset:", formData);
        }, 1500);
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '12px', padding: 1 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, position: 'relative' }}>
                <CloudUploadIcon sx={{ color: colors.grey[500], fontSize: 28 }} />
                <Box>
                    <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                        Upload New Dataset
                    </Typography>
                    <Typography variant="caption" color={colors.grey[300]}>
                        Contribute data to the federated network
                    </Typography>
                </Box>
                <IconButton 
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: colors.grey[500]
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers sx={{ borderBottom: 'none' }}>
                <Alert 
                    severity="warning" 
                    variant="outlined"
                    icon={<VerifiedUserOutlinedIcon fontSize="inherit" color={colors.secondary[500]} sx={{ color: colors.secondary[500] }} />}
                    sx={{ mb: 3, border: `1px solid ${colors.secondary[500]}`, bgcolor: `${colors.grey[900]}`, color: `${colors.secondary[500]}` }}
                >
                    <Typography variant="subtitle2" fontWeight="bold">
                        Standardized Schema Required
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, color: colors.grey[500] }}>
                        To ensure interoperability, uploaded datasets must adhere to a supported data schema — currently the Observational Medical Outcomes Partnership (OMOP) Common Data Model, with more formats planned. Non-compliant datasets will be rejected during validation.
                    </Typography>
                </Alert>

                <Grid container spacing={3}>
                    {/* Basic Info */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Dataset Title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            variant="outlined"
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            multiline
                            rows={4}
                            variant="outlined"
                            placeholder="Describe the dataset's purpose, origin, and contents..."
                        />
                    </Grid>

                    {/* Metadata Dropdowns */}
                    <Grid item xs={12} md={4}>
                         <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                name="category"
                                value={formData.category}
                                label="Category"
                                onChange={handleChange}
                            >
                                <MenuItem value="Oncology">Oncology</MenuItem>
                                <MenuItem value="Pharmacology">Pharmacology</MenuItem>
                                <MenuItem value="Epidemiology">Epidemiology</MenuItem>
                                <MenuItem value="Medical Imaging">Medical Imaging</MenuItem>
                                <MenuItem value="Genomics">Genomics</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                         <FormControl fullWidth>
                            <InputLabel>Privacy Level</InputLabel>
                            <Select
                                name="privacy"
                                value={formData.privacy}
                                label="Privacy Level"
                                onChange={handleChange}
                            >
                                <MenuItem value="De-identified">De-identified</MenuItem>
                                <MenuItem value="Synthetic">Synthetic</MenuItem>
                                <MenuItem value="Public">Public</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                         <FormControl fullWidth>
                            <InputLabel>Institution Type</InputLabel>
                            <Select
                                name="institution"
                                value={formData.institution}
                                label="Institution Type"
                                onChange={handleChange}
                            >
                                <MenuItem value="Hospital">Hospital</MenuItem>
                                <MenuItem value="Industry">Industry</MenuItem>
                                <MenuItem value="Research">Research Lab</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* File Upload Area */}
                    <Grid item xs={12}>
                        <Box sx={{ 
                            border: `2px dashed ${colors.grey[800]}`, 
                            borderRadius: '8px', 
                            p: 3, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: 1, 
                            bgcolor: 'white',
                            cursor: 'pointer',
                            '&:hover': { borderColor: colors.grey[500] }
                        }}
                        component="label"
                        >
                            <input 
                                type="file" 
                                hidden 
                                accept=".csv,.zip,.json" 
                                onChange={(e) => {
                                    if(e.target.files && e.target.files[0]) {
                                        setFormData(prev => ({ ...prev, file: e.target.files[0] }));
                                    }
                                }} 
                            />
                            {formData.file ? (
                                <>
                                    <CloudUploadIcon sx={{ fontSize: 40, color: colors.grey[500] }} />
                                    <Typography variant="h6" color={colors.grey[100]}>
                                        {formData.file.name}
                                    </Typography>
                                    <Typography variant="body2" color={colors.grey[300]}>
                                        {(formData.file.size / 1024).toFixed(2)} KB • Click to change
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <CloudUploadIcon sx={{ fontSize: 40, color: colors.grey[400] }} />
                                    <Typography variant="h6" color={colors.grey[100]}>
                                        Drag & drop your OMOP file here
                                    </Typography>
                                    <Typography variant="body2" color={colors.grey[300]}>
                                        or click to browse (.csv, .zip)
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>

            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ color: colors.grey[100] }}>
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleUpload}
                    startIcon={<CloudUploadIcon />}
                    disabled={!formData.title || !formData.file || loading}
                    sx={{ 
                        bgcolor: colors.grey[600],
                        '&:hover': { bgcolor: colors.grey[700] }
                    }}
                >
                    {loading ? "Uploading..." : "Upload Dataset"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadDatasetModal;
